import BottomBar from "@components/BottomBars/BottomBar";
import BottomBarDesktop from "@components/BottomBars/BottomBarDesktop";
import CardInformation from "@components/Cards/CardInformation";
import ModalCalculatorHowWorks from "@components/Modals/ModalCalculatorHowWorks";
import ModalSaveQuote from "@components/Modals/ModalSaveQuote";
import Footer from "@components/layout/Footer";
import { useViewportSize } from "@mantine/hooks";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

type PensionStepTwoProps = {
  results: CalculationResults;
};

type CalculationResults = {
  how_much: number;
  max_monthly_income: number;
  max_premium: number;
  monthly_income_including_state_pension: number;
  premium_needed: number;
  tax_relief: number;
};

const PensionStepTwo: React.FC<PensionStepTwoProps> = ({ results }) => {
  const navigate = useNavigate();
  const [openLearnMore, setOpenLearnMore] = useState(false);
  const [showMoreHowWorks, setShowMoreHowWorks] = useState(false);
  const [openSaveQuote, setOpenSaveQuote] = useState(false);
  const { width: widthScreen } = useViewportSize();

  const onGetStartedClick = () => {
    window.scrollTo(0, 0);
    navigate("/signup");
  };

  const handleQuote = useMemo(() => {
    const handleOpenModalQuote = () => {
      setOpenSaveQuote(true);
    };

    if (widthScreen < 768) {
      return "/pension-calculator/quote";
    } else {
      return handleOpenModalQuote;
    }
  }, [widthScreen]);

  return (
    <>
      <ModalCalculatorHowWorks
        open={openLearnMore}
        onClose={() => setOpenLearnMore(false)}
      />

      <ModalSaveQuote
        open={openSaveQuote}
        onClose={() => setOpenSaveQuote(false)}
      />

      <div className="p-4 w-full block md:hidden">
        <span className="text-pn-large-title">
          Here's what we calculated for you
        </span>
      </div>

      <div className="flex flex-col md:flex-row px-4 py-0 md:px-6 md:py-6 items-center gap-y-4 md:gap-y-0 gap-x-0 md:gap-x-6">
        <div className=" w-full border border-pn_gray-200 bg-pn_white flex flex-col p-6 gap-y-4 rounded-2xl">
          <div className="flex flex-col gap-y-4">
            <span className="text-[13px] font-semibold uppercase bg-[#F4E34E] w-fit">
              enjoy
            </span>
            <div className="flex flex-col gap-y-2">
              <span className="text-pn-large-title">
                €{results.how_much.toLocaleString()}
              </span>
              <span className="text-pn-callout-regular text-[#57534E]">
                per month
              </span>
            </div>
          </div>

          <div className="divider-dotted-gray"></div>

          <div className="flex flex-col gap-y-3">
            <div className="flex flex-col gap-y-[11px]">
              <span className="text-[13px] font-semibold uppercase bg-[#F4E34E] w-fit">
                When you Save
              </span>
              <div className="flex flex-col gap-y-1">
                <span className="text-pn-large-title">
                  €{results.premium_needed.toLocaleString()}
                </span>
                <span className="text-pn-callout-regular text-[#57534E]">
                  per month
                </span>
              </div>
            </div>
            <div className="text-pn-body-regular">
              With tax relief at{" "}
              <span className="text-pn-body-emphasis">40%</span> you will pay as
              little as{" "}
              <span className="text-pn-body-emphasis">
                €{results.tax_relief.toLocaleString()} a month
              </span>{" "}
              if you are eligible
            </div>
          </div>

          <div className="pt-4 block md:hidden">
            <BottomBar
              layout="stacked_back"
              padding={false}
              bordered={false}
              onNext={onGetStartedClick}
              labelNext="Start My Pension"
              onBack={handleQuote}
              labelBack="Save Quote"
            />
          </div>
        </div>

        <div className="py-4 md:py-6 px-0 md:px-6 flex flex-col gap-y-6">
          <span className="text-xl font-semibold text-[#1C1C1E]">
            Follow these 4 simple steps to start a digital pension with Penny in
            just 24 hours
          </span>
          <div className="flex flex-col gap-y-4">
            <div className="flex items-center gap-x-6">
              <div className="bg-[#F4E34E] rounded-full w-[32px] h-[32px] flex items-center justify-center">
                <span className="text-pn-button-1">1</span>
              </div>
              <div className="text-pn-body-regular flex-1">
                <Link
                  className="text-pn-body-link text-pn_cyan-500"
                  to={"/signup"}
                >
                  Create your profile
                </Link>{" "}
              </div>
            </div>

            <div className="flex items-center gap-x-6">
              <div className="bg-[#F4E34E] rounded-full w-[32px] h-[32px] flex items-center justify-center">
                <span className="text-pn-button-1">2</span>
              </div>
              <div className="text-pn-body-regular flex-1">
                Answer a few simple questions
              </div>
            </div>

            <div className="flex items-center gap-x-6">
              <div className="bg-[#F4E34E] rounded-full w-[32px] h-[32px] flex items-center justify-center">
                <span className="text-pn-button-1">3</span>
              </div>
              <div className="text-pn-body-regular flex-1">
                Book a call with your advisor
              </div>
            </div>

            <div className="flex items-center gap-x-6">
              <div className="bg-[#F4E34E] rounded-full w-[32px] h-[32px] flex items-center justify-center">
                <span className="text-pn-button-1">4</span>
              </div>
              <div className="text-pn-body-regular flex-1">
                Complete pension application
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pb-2 md:pb-2 pt-4 md:pt-0 px-4 md:px-6">
        <CardInformation
          variant="info"
          showMoreToggle={showMoreHowWorks}
          showMoreContent={
            <div className="p-4 text-pn-callout-regular">
              This calculator is designed to give an indication of the monthly
              pension contribution you may need to mak in order to achieve your
              target monthly income in retirement. It is important to note that
              the calculator takes into account your target monthly income
              includes the Social Welfare pension, which Zurich Life assumes you
              will be entitled to when you retire.The calculations are in line
              with Zurich lIfe's Understating of Guidelines issues by the
              Society of Actuaries in Ireland (Actuarial Standard Practice
              Pen-12 . Version 1.7) and may change in the future. These figures
              are for illustrative purposes only and are not an offer of
              contract.These figures are estimates only. They are not a reliable
              guide to the future performance of your investment. Actual
              Investment growth will depend on the performance of the underlying
              investments and may be more or less than illustrated.
            </div>
          }
          description={
            <div className="text-callout-regular">
              Read more about how the calculator works.{" "}
              <button
                onClick={() => setShowMoreHowWorks((prev) => !prev)}
                className="inline-block md:hidden text-pn-callout-link text-pn_cyan-500"
              >
                {showMoreHowWorks ? "Show less" : "Show more"}
              </button>
              <button
                onClick={() => setOpenLearnMore(true)}
                className="hidden md:inline-block text-pn-callout-link text-pn_cyan-500"
              >
                Learn More
              </button>
            </div>
          }
        />
      </div>

      <div className="hidden md:block">
        <BottomBarDesktop
          message="Your information is secure with Penny"
          onNext={() => {}}
          onBack={handleQuote}
          labelBack="Save Quote"
          labelNext="Start My Pension"
          insideCard
        />
      </div>

      <div className="px-4 block md:hidden">
        <div className="p-6 mt-6 bg-pn_white rounded-lg flex flex-col gap-y-6">
          <span className="text-center text-pn-large-title">
            Start your pension process now
          </span>
          <button
            className="btn-primary-lg"
            onClick={() => onGetStartedClick()}
          >
            Get Started
          </button>
        </div>
      </div>

      <div className="block md:hidden">
        <Footer />
      </div>
    </>
  );
};
export default PensionStepTwo;
